import { lazy, Suspense, type ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';

// every page is split out, so the first paint does not carry the whole product
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const IngredientsPage = lazy(() => import('@/features/ingredients/IngredientsPage'));
const IngredientDetailPage = lazy(() => import('@/features/ingredients/IngredientDetailPage'));
const DishesPage = lazy(() => import('@/features/dishes/DishesPage'));
const DishEditorPage = lazy(() => import('@/features/dishes/DishEditorPage'));
const MenusPage = lazy(() => import('@/features/menus/MenusPage'));
const MenuBuilderPage = lazy(() => import('@/features/menus/MenuBuilderPage'));
const OrdersPage = lazy(() => import('@/features/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/features/orders/OrderDetailPage'));
const ClientsPage = lazy(() => import('@/features/orders/ClientsPage'));
const ProcurementPage = lazy(() => import('@/features/procurement/ProcurementPage'));
const ProcurementDetailPage = lazy(
  () => import('@/features/procurement/ProcurementDetailPage'),
);
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));

const Loading = () => (
  <Box className="flex h-64 items-center justify-center">
    <CircularProgress />
  </Box>
);

const page = (element: ReactNode) => <Suspense fallback={<Loading />}>{element}</Suspense>;

export interface AppRoute {
  path: string;
  index?: boolean;
  element: ReactNode;
  /** Permission required to open the route at all. */
  permission?: string;
}

export const routes: AppRoute[] = [
  { path: '/', index: true, element: page(<DashboardPage />) },
  { path: 'ingredients', element: page(<IngredientsPage />), permission: 'ingredient:read' },
  {
    path: 'ingredients/:id',
    element: page(<IngredientDetailPage />),
    permission: 'ingredient:read',
  },
  { path: 'dishes', element: page(<DishesPage />), permission: 'dish:read' },
  { path: 'dishes/:id', element: page(<DishEditorPage />), permission: 'dish:read' },
  {
    path: 'dishes/:id/versions/:versionId',
    element: page(<DishEditorPage />),
    permission: 'dish:read',
  },
  { path: 'menus', element: page(<MenusPage />), permission: 'menu:read' },
  { path: 'menus/:id', element: page(<MenuBuilderPage />), permission: 'menu:read' },
  { path: 'orders', element: page(<OrdersPage />), permission: 'order:read' },
  { path: 'orders/:id', element: page(<OrderDetailPage />), permission: 'order:read' },
  { path: 'clients', element: page(<ClientsPage />), permission: 'client:read' },
  { path: 'procurement', element: page(<ProcurementPage />), permission: 'procurement:read' },
  {
    path: 'procurement/:id',
    element: page(<ProcurementDetailPage />),
    permission: 'procurement:read',
  },
  { path: 'reports', element: page(<ReportsPage />), permission: 'report:read' },
  { path: 'admin', element: page(<AdminPage />), permission: 'reference:read' },
];
